import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { TriangleAlert } from 'lucide-react';
import { api } from '../lib/api';
import { staffQuery } from '../lib/staff';
import { CoinMascot } from '../components/Coin';
import { CopyButton } from '../components/ui';
import { Turnstile } from '../components/Turnstile';

function Shell({
  title,
  intro,
  children,
}: {
  title: string;
  intro?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    // overflow-x-clip: the mascot pokes past the card edge without widening a phone screen.
    <div className="grid min-h-screen place-items-center overflow-x-clip px-4 py-10">
      <main className="w-full max-w-[420px]">
        <div className="relative rounded-[24px] border-[1.5px] border-ink bg-paper p-6 shadow-[5px_5px_0_#111110] sm:p-8">
          <CoinMascot size={72} rotate={14} className="absolute -right-4 -top-7 sm:-right-6" />
          <div className="mb-7 flex items-center gap-2">
            <img src="/brand/logo-48.png" alt="" className="h-8 w-8" />
            <span className="text-[18px] font-extrabold tracking-[-0.03em]">GaintPlay</span>
            <span className="rounded-full bg-ink px-2 py-0.5 text-[11px] font-bold text-white">
              Admin
            </span>
          </div>
          <h1 className="text-[28px] font-extrabold leading-tight tracking-[-0.025em]">{title}</h1>
          {intro && <p className="mt-1.5 text-[14px] leading-relaxed text-graphite">{intro}</p>}
          <div className="mt-6">{children}</div>
        </div>
        <p className="mt-5 text-center text-[12px] text-stone">
          Staff only. Every sign-in and action is recorded in the audit log.
        </p>
      </main>
    </div>
  );
}

function Field({
  id,
  label,
  hint,
  children,
}: {
  id: string;
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <div className="flex items-baseline justify-between gap-3">
        <label className="label" htmlFor={id}>
          {label}
        </label>
        {hint && <span className="mb-1.5 text-[12px] text-stone">{hint}</span>}
      </div>
      {children}
    </div>
  );
}

function FormError({ text }: { text: string | null }) {
  if (!text) return null;
  return (
    <p
      role="alert"
      className="flex items-start gap-2 rounded-[10px] bg-berry/[0.07] px-3 py-2.5 text-[14px] text-berry-deep"
    >
      <TriangleAlert size={16} className="mt-0.5 shrink-0" aria-hidden="true" />
      <span className="min-w-0 break-words">{text}</span>
    </p>
  );
}

/** Where to go after signing in: the page that sent us here, if it is one of ours. */
function nextPage(next: string | null): string {
  // One leading slash only ("//x" and "/\x" are other hosts to a browser), no control characters.
  return next &&
    /^\/(?![/\\])/.test(next) &&
    !/[\u0000-\u001f\u007f]/.test(next) &&
    !next.startsWith('/login')
    ? next
    : '/';
}

export function Login() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [f, setF] = useState({ email: '', password: '', code: '' });
  const [err, setErr] = useState<string | null>(null);
  const [setupMode, setSetupMode] = useState(false);
  const [sent, setSent] = useState(false);
  // The human check (Cloudflare Turnstile), when the API has a site key. A token works once, so a
  // refused attempt gets a fresh widget.
  const siteKey = useQuery({
    queryKey: ['auth-config'],
    queryFn: () => api<{ turnstileSiteKey: string | null }>('/auth/config'),
    staleTime: Infinity,
  }).data?.turnstileSiteKey;
  const [turnstileToken, setTurnstileToken] = useState<string | undefined>();
  const [round, setRound] = useState(0);
  // A widget that can't load doesn't block the button: the API decides (it only insists on a token
  // once TURNSTILE_SECRET is set), so a misconfigured widget can't lock the team out by itself.
  const [checkUnavailable, setCheckUnavailable] = useState(false);
  const onUnavailable = useCallback(() => setCheckUnavailable(true), []);
  const waiting = !!siteKey && !turnstileToken && !checkUnavailable;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setErr(null);
    try {
      if (setupMode) {
        await api('/auth/setup-request', { body: { email: f.email, turnstileToken } });
        setSent(true);
        return;
      }
      await api('/auth/login', { body: { ...f, turnstileToken } });
      // Drop everything cached while signed out (the Shell would read "not signed in" from it and
      // send us straight back here), then confirm the new session before opening the dashboard.
      qc.clear();
      const staff = await qc.fetchQuery(staffQuery);
      if (!staff) {
        setErr(
          'Signed in, but this browser didn’t keep the session. Allow cookies for admin.gaintplay.com and try again.',
        );
        return;
      }
      navigate(nextPage(params.get('next')), { replace: true });
    } catch (e2) {
      setErr((e2 as Error).message);
      if (siteKey) {
        setTurnstileToken(undefined);
        setRound((r) => r + 1);
      }
    }
  };

  return (
    <Shell
      title={setupMode ? 'First-time setup' : 'Sign in'}
      intro={
        setupMode
          ? 'For the configured admin address only. We email a one-time link to set a password and 2FA.'
          : undefined
      }
    >
      {sent ? (
        <p
          role="status"
          className="rounded-[12px] bg-canvas px-4 py-3 text-[14px] leading-relaxed text-graphite"
        >
          If that address is the configured admin, a setup link is on its way. It expires in 1 hour.
        </p>
      ) : (
        <form onSubmit={submit} className="space-y-4">
          <Field id="login-email" label="Email">
            <input
              id="login-email"
              className="input"
              type="email"
              autoComplete="username"
              required
              value={f.email}
              onChange={(e) => setF({ ...f, email: e.target.value })}
            />
          </Field>
          {!setupMode && (
            <>
              <Field id="login-password" label="Password">
                <input
                  id="login-password"
                  className="input"
                  type="password"
                  autoComplete="current-password"
                  required
                  value={f.password}
                  onChange={(e) => setF({ ...f, password: e.target.value })}
                />
              </Field>
              <Field id="login-code" label="2FA code" hint="6 digits from your authenticator">
                <input
                  id="login-code"
                  className="input font-semibold tabular-nums tracking-[0.4em] placeholder:font-normal placeholder:tracking-[0.4em]"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  placeholder="••••••"
                  maxLength={6}
                  required
                  value={f.code}
                  onChange={(e) => setF({ ...f, code: e.target.value.replace(/\D/g, '') })}
                />
              </Field>
            </>
          )}
          <Turnstile
            key={round}
            siteKey={siteKey}
            onToken={setTurnstileToken}
            onUnavailable={onUnavailable}
          />
          <FormError text={err} />
          <button className="btn-primary w-full" disabled={waiting}>
            {setupMode ? 'Email me a setup link' : 'Sign in'}
          </button>
        </form>
      )}
      <button
        type="button"
        className="-mb-2 mt-3 py-2 text-[13px] font-semibold text-stone underline underline-offset-2 hover:text-ink"
        onClick={() => {
          setSetupMode(!setupMode);
          setSent(false);
          setErr(null);
        }}
      >
        {setupMode ? 'Back to sign in' : 'First time? Set up the admin account'}
      </button>
    </Shell>
  );
}

function Step({ n, title, children }: { n: number; title: string; children: React.ReactNode }) {
  return (
    <li className="grid grid-cols-[24px_minmax(0,1fr)] gap-x-3">
      <span
        aria-hidden="true"
        className="grid h-6 w-6 place-items-center rounded-full bg-ink text-[12px] font-bold text-white"
      >
        {n}
      </span>
      <div className="min-w-0">
        <div className="pt-0.5 text-[14px] font-bold">{title}</div>
        <div className="mt-2">{children}</div>
      </div>
    </li>
  );
}

export function Setup() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token') ?? '';
  const [totp, setTotp] = useState<{ email: string; secret: string; uri: string } | null>(null);
  const [f, setF] = useState({ name: '', password: '', code: '' });
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    api<{ email: string; name: string; secret: string; uri: string }>('/auth/setup/totp', {
      body: { token },
    })
      .then((r) => {
        setTotp(r);
        // The name they were invited under, to keep or change.
        if (r.name) setF((x) => ({ ...x, name: x.name || r.name }));
      })
      .catch((e: Error) => setErr(e.message));
  }, [token]);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setErr(null);
    try {
      await api('/auth/setup', { body: { token, ...f } });
      navigate('/login');
    } catch (e2) {
      setErr((e2 as Error).message);
    }
  };

  return (
    <Shell
      title="Set up your admin account"
      intro={
        totp?.email ? (
          <>
            For <b className="font-semibold text-ink">{totp.email}</b>
          </>
        ) : undefined
      }
    >
      {!totp ? (
        err ? (
          <FormError text={err} />
        ) : (
          <p role="status" className="text-[14px] text-stone">
            Checking your setup link…
          </p>
        )
      ) : (
        <form onSubmit={submit}>
          <ol className="space-y-6">
            <Step n={1} title="Add GaintPlay to your authenticator">
              <p className="text-[13px] leading-relaxed text-graphite">
                In Google Authenticator, 1Password or Authy, add an account with this key:
              </p>
              <div className="mt-2 flex items-center gap-1 rounded-[10px] border border-hairline bg-canvas py-1.5 pl-3 pr-1.5">
                <code className="min-w-0 flex-1 select-all break-all font-mono text-[14px] font-semibold tracking-wide text-ink">
                  {totp.secret}
                </code>
                {totp.secret && <CopyButton text={totp.secret} label="Copy" />}
              </div>
              {totp.uri && (
                <a
                  href={totp.uri}
                  className="mt-2 inline-block py-1 text-[13px] font-semibold text-stone underline underline-offset-2 hover:text-ink"
                >
                  On this phone? Open in your authenticator app
                </a>
              )}
            </Step>
            <Step n={2} title="Choose your name and password">
              <div className="space-y-3">
                <Field id="setup-name" label="Your name">
                  <input
                    id="setup-name"
                    className="input"
                    autoComplete="name"
                    required
                    value={f.name}
                    onChange={(e) => setF({ ...f, name: e.target.value })}
                  />
                </Field>
                <Field id="setup-password" label="Password" hint="12+ characters">
                  <input
                    id="setup-password"
                    className="input"
                    type="password"
                    autoComplete="new-password"
                    minLength={12}
                    required
                    value={f.password}
                    onChange={(e) => setF({ ...f, password: e.target.value })}
                  />
                </Field>
              </div>
            </Step>
            <Step n={3} title="Confirm with a code from the app">
              <Field id="setup-code" label="6-digit code">
                <input
                  id="setup-code"
                  className="input font-semibold tabular-nums tracking-[0.4em] placeholder:font-normal placeholder:tracking-[0.4em]"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  placeholder="••••••"
                  maxLength={6}
                  required
                  value={f.code}
                  onChange={(e) => setF({ ...f, code: e.target.value.replace(/\D/g, '') })}
                />
              </Field>
            </Step>
          </ol>
          <div className="mt-6 space-y-3">
            <FormError text={err} />
            <button className="btn-primary w-full">Finish setup</button>
          </div>
        </form>
      )}
    </Shell>
  );
}
